import { WorkPage, WorkSection, WorkEmpty } from '../components/work-page.tsx';
import { TableScroll } from '@web/components/table-scroll.tsx';
import { Fragment, useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocalizedContent } from '../i18n/localized.ts';
import { customerApi, invoiceApi, paymentApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { PlanRequiredPage, usePlanFeature } from '../components/plan-gate.tsx';
import { useCommercialWork } from '../components/commercial-gate.tsx';
import {
  Alert,
  Button,
  Checkbox,
  classes,
  ConfirmDialog,
  LoadingBlock,
  Pagination,
  Select,
  TextInput,
} from '../components/ui.tsx';
import SearchSelect from '../components/search-select.tsx';
import { currencyOptions } from '../forms/currencies.ts';
import { normalizeTypedRate } from '@shared/exchange-rates.ts';
import { MoneyInput } from '../components/money-input.tsx';
import { PdfDownloadButton } from '../components/pdf-download.tsx';
import AddressLink from '../components/address-link.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import { refundableCentsForCharge } from '@shared/calculations/payments.ts';
import { lineItemsTotalCents, MAX_INVOICE_LINE_ITEMS } from '@shared/schemas/invoices.ts';
import { formatMinutes } from '../i18n/format.ts';
import {
  formatCompanyInstant,
  formatLocalTime,
  formatNumericDate,
  formatNumericInstant,
  formatShortDate,
} from '../i18n/datetime.ts';
import { getDateLocale } from '../i18n/locale.ts';
import { INVOICE_STATUSES } from '@shared/constants/index.ts';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';
import type {
  CompanyPaymentMethod,
  Invoice,
  InvoiceBatchSendResult,
  MonthlyInvoice,
  Payment,
} from '@shared/types/index.ts';

/**
 * Same tones `PaymentBadge` uses for the six statuses an invoice shares with
 * `appointments.payment_status`, plus `void` — a state only an invoice has
 * (the cleaning behind it was canceled), so it is not in that shared palette.
 */
const TONES: Record<Invoice['status'], string> = {
  not_billed: 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
  pending: 'border-[color:var(--color-warning)] text-[color:var(--color-warning)]',
  paid: 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]',
  failed: 'border-[color:var(--color-danger)] text-[color:var(--color-danger)]',
  refunded: 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
  partially_refunded: 'border-[color:var(--color-warning)] text-[color:var(--color-warning)]',
  void: 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
};

function InvoiceStatusBadge({ status }: { status: Invoice['status'] }) {
  const { t } = useTranslation();
  const label = status === 'void' ? t('invoices.list.statusVoid') : t(`payments.appointmentStatus.${status}`);
  return <span className={classes('badge', TONES[status])}>{label}</span>;
}

/**
 * The two ways an invoice gets paid, right on its row: charging the card the
 * customer already has on file (a manual trigger — the same charge automatic
 * billing fires on its own schedule), or recording money the office collected
 * some other way. Hidden once the invoice is no longer billable, the same
 * rule `AppointmentPayments` uses for its own charge button.
 *
 * "Charge card" needs BOTH halves of its name to be true: a connected
 * processor, and a card belonging to this invoice's customer. It used to ask
 * only the first, so every unpaid row on a company with payments switched on
 * offered to charge a card — including the customers who have never saved one,
 * where pressing it could only ever return "This customer has no card on file".
 * Recording a payment is unconditional beside it, because cash and checks
 * need neither.
 */
function InvoicePayActions({
  invoice,
  configured,
  tipsEnabled,
  methods,
  companyCurrency,
  onDone,
  balanceCents,
}: {
  invoice: Invoice;
  configured: boolean;
  /** Whether this location offers tips — the server refuses one anywhere else. */
  tipsEnabled: boolean;
  methods: CompanyPaymentMethod[];
  companyCurrency: string;
  onDone: (message: string) => void;
  /**
   * What is actually still owed, when the caller knows it — the detail panel
   * does, the list row does not. With it, the actions stay offered on a
   * partly-paid manual invoice (whose status already reads `paid` the moment
   * any money lands) and every number shown is the balance, which is what the
   * endpoints charge.
   */
  balanceCents?: number;
}) {
  const { t } = useTranslation();
  /*
    The company's own payment-method labels, read by whoever is recording the
    money — a seeded, translated library like the room and task ones, so it
    follows the reader (see `localized-text.ts`).
  */
  const localized = useLocalizedContent();
  const manual = invoice.kind === 'manual';
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * What the form offers to collect: the balance where the caller knows it,
   * the whole document where it does not. Always in the document's own
   * currency, which is what the payment is recorded in.
   */
  const defaultAmount = balanceCents ?? (manual ? invoice.totalCents : invoice.priceCents);
  const [amount, setAmount] = useState<number | null>(defaultAmount);
  /**
   * The balance the amount above was seeded from, so a payment that changes
   * it reseeds the box.
   *
   * Without this, recording $40 against a $100 manual invoice and then
   * reopening the form offers $100 again — this component stays mounted
   * across the refetch, so the new balance reaches it as a prop and nothing
   * else. The endpoint takes the amount as typed (the balance only caps an
   * omitted one), so submitting that second $100 is an overpaid ledger. The
   * balance is the honest test, never the stored status: a partly paid manual
   * invoice's status is not a reliable guide to what is still owed.
   */
  const [seededBalance, setSeededBalance] = useState(balanceCents);
  if (balanceCents !== seededBalance) {
    setSeededBalance(balanceCents);
    setAmount(defaultAmount);
  }
  const [tip, setTip] = useState<number | null>(null);
  const [methodId, setMethodId] = useState('');
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const documentCurrency = invoice.currencyCode ?? companyCurrency;
  /**
   * Money handed over in another currency (0115): euros against a dollar
   * invoice. Off by default; when on, the amount received and its currency
   * replace the amount above, and the Worker converts it at the typed rate or
   * the company's kept one.
   */
  const [foreign, setForeign] = useState<{ currencyCode: string; amountCents: number | null; rate: string } | null>(
    null,
  );

  function fail(caught: unknown) {
    setError(caught instanceof ApiRequestError ? caught.message : t('invoices.list.actionFailed'));
  }

  const charge = useMutation({
    mutationFn: () =>
      manual
        ? paymentApi.chargeInvoice(invoice.id, {})
        : paymentApi.chargeAppointment(invoice.appointmentId as string, {}),
    onSuccess: (payment) =>
      onDone(
        payment.status === 'succeeded'
          ? t('invoices.list.cardCharged')
          : t('invoices.list.chargeStatus', { status: t(`payments.status.${payment.status}`).toLowerCase() }),
      ),
    onError: fail,
  });

  const record = useMutation({
    mutationFn: () =>
      paymentApi.recordPayment({
        customerId: invoice.customerId,
        ...(manual ? { invoiceId: invoice.id } : { appointmentId: invoice.appointmentId }),
        ...(foreign && foreign.amountCents !== null
          ? {
              received: {
                currencyCode: foreign.currencyCode,
                amountCents: foreign.amountCents,
                ...(foreign.rate.trim() ? { exchangeRate: normalizeTypedRate(foreign.rate) } : {}),
              },
            }
          : amount === null
            ? {}
            : { amountCents: amount }),
        ...(tip !== null && tip > 0 ? { tipCents: tip } : {}),
        methodId,
        ...(note.trim() ? { description: note.trim() } : {}),
        ...(date ? { receivedOn: date } : {}),
      }),
    onSuccess: () => {
      setRecording(false);
      setNote('');
      setDate('');
      setTip(null);
      setForeign(null);
      onDone(t('invoices.list.paymentRecorded'));
    },
    onError: fail,
  });

  // A credit memo owes money the other way — its actions are
  // `CreditRefundActions`, never these.
  if (isCreditMemo(invoice)) return null;
  // With the balance in hand (the detail panel), the honest test is the
  // money: anything still owed keeps the actions, nothing owed removes them.
  // Without it, the row falls back to the statuses that can only mean unpaid.
  if (balanceCents !== undefined) {
    if (balanceCents <= 0 || invoice.status === 'void') return null;
  } else if (invoice.status !== 'not_billed' && invoice.status !== 'failed') {
    return null;
  }

  return (
    <div>
      {error ? <p className="mb-1 text-xs text-[color:var(--color-danger)]">{error}</p> : null}
      <div className="flex flex-wrap gap-2">
        {configured && invoice.hasCardOnFile ? (
          <Button
            variant="ghost"
            busy={charge.isPending}
            onClick={() => {
              setError(null);
              // Charging is irreversible, and this row is one mis-click on a
              // list. Same confirm-with-amount the visit's own billing card
              // uses; the fee note stands in for the exact fee-inclusive
              // total, which this list does not load per row.
              //
              // The number promised is what the endpoint actually charges —
              // the visit's price, a manual invoice's balance (its total,
              // until anything is collected) — never the record-payment
              // form's editable amount, which prices a different action. It is
              // charged in the document's own currency.
              const cardAmount = balanceCents ?? (manual ? invoice.totalCents : invoice.priceCents);
              const confirmed = window.confirm(
                cardAmount !== null
                  ? t('invoices.list.confirmCharge', {
                      amount: formatCents(cardAmount, invoice.currencyCode ?? companyCurrency, getDateLocale()),
                    })
                  : t('invoices.list.confirmChargeNoAmount'),
              );
              if (confirmed) charge.mutate();
            }}
          >
            {t('invoices.list.chargeCard')}
          </Button>
        ) : null}
        <Button
          variant="ghost"
          onClick={() => {
            setError(null);
            setRecording((value) => !value);
          }}
        >
          {recording ? t('invoices.list.recordCancel') : t('invoices.list.recordPayment')}
        </Button>
      </div>
      {recording ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!methodId) return;
            record.mutate();
          }}
          className="mt-2 grid min-w-72 gap-2 rounded-md border border-[color:var(--color-line)] p-3"
        >
          {foreign ? (
            <>
              <Select
                label={t('invoices.list.receivedCurrencyLabel')}
                value={foreign.currencyCode}
                options={currencyOptions(foreign.currencyCode).filter((option) => option.value !== documentCurrency)}
                // The amount is in the old currency's units (¥ has no cents) and
                // the rate is the old pair's, so a new currency starts both again.
                onChange={(event) =>
                  setForeign({ ...foreign, currencyCode: event.target.value, amountCents: null, rate: '' })
                }
              />
              <MoneyInput
                label={t('invoices.list.receivedAmountLabel')}
                valueCents={foreign.amountCents}
                onChangeCents={(cents) => setForeign({ ...foreign, amountCents: cents })}
                currencyCode={foreign.currencyCode}
              />
              <TextInput
                label={t('invoices.list.receivedRateLabel', { from: foreign.currencyCode, to: documentCurrency })}
                hint={t('invoices.list.receivedRateHint')}
                inputMode="decimal"
                value={foreign.rate}
                onChange={(event) => setForeign({ ...foreign, rate: event.target.value })}
              />
            </>
          ) : (
            <MoneyInput
              label={
                tipsEnabled ? t('appointmentPayments.recordAmountBeforeTipLabel') : t('invoices.list.recordAmountLabel')
              }
              valueCents={amount}
              onChangeCents={setAmount}
              // Recorded in the invoice's own currency, as the Worker records it.
              currencyCode={documentCurrency}
            />
          )}
          <Checkbox
            label={t('invoices.list.receivedForeign', { currency: documentCurrency })}
            checked={foreign !== null}
            onChange={(event) =>
              setForeign(
                event.target.checked
                  ? {
                      currencyCode: documentCurrency === 'EUR' ? 'USD' : 'EUR',
                      amountCents: null,
                      rate: '',
                    }
                  : null,
              )
            }
          />
          {tipsEnabled ? (
            <MoneyInput
              label={t('appointmentPayments.recordTipLabel')}
              hint={t('appointmentPayments.recordTipHint')}
              valueCents={tip}
              onChangeCents={setTip}
              currencyCode={documentCurrency}
            />
          ) : null}
          <Select
            label={t('invoices.list.recordMethodLabel')}
            value={methodId}
            onChange={(event) => setMethodId(event.target.value)}
            options={[
              { value: '', label: t('invoices.list.recordMethodPlaceholder') },
              ...methods.map((method) => ({
                value: method.id,
                label: localized.text(method.name, method.nameTranslations),
              })),
            ]}
          />
          <TextInput
            label={t('invoices.list.recordDateLabel')}
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
          <TextInput
            label={t('invoices.list.recordNoteLabel')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t('invoices.list.recordNotePlaceholder')}
            maxLength={200}
          />
          <Button
            type="submit"
            variant="primary"
            busy={record.isPending}
            disabled={
              !methodId ||
              (foreign ? foreign.amountCents === null || foreign.amountCents <= 0 : amount === null || amount <= 0)
            }
          >
            {t('invoices.list.recordSubmit')}
          </Button>
        </form>
      ) : null}
    </div>
  );
}

/**
 * Settling a credit memo: giving the money back. Two ways, one form — back
 * onto one of the customer's past card payments (what "returns can go on
 * cards" means in practice), or by hand through one of the company's own
 * methods, cash from the drawer or a check in the mail.
 *
 * The choices are fetched only when the form opens: the customer's ledger
 * says which charges still have money to give back, and this invoice's own
 * ledger says how much of the credit is left to return.
 */
function CreditRefundActions({
  invoice,
  methods,
  companyCurrency,
  onDone,
}: {
  invoice: Invoice;
  methods: CompanyPaymentMethod[];
  companyCurrency: string;
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation();
  /*
    The company's own payment-method labels, read by whoever is recording the
    money — a seeded, translated library like the room and task ones, so it
    follows the reader (see `localized-text.ts`).
  */
  const localized = useLocalizedContent();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState('');
  const [amount, setAmount] = useState<number | null>(null);
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Money going back cannot be recalled, and a blank amount means all of it,
  // so a submitted form (a phone keyboard's Go included) only asks first —
  // holding the figure the question names, which is what is then sent.
  const [confirming, setConfirming] = useState<number | null>(null);

  const ledger = useQuery({
    queryKey: ['invoice-ledger', invoice.id],
    queryFn: () => paymentApi.invoicePayments(invoice.id),
    enabled: open,
  });
  const customerPayments = useQuery({
    queryKey: ['payments', invoice.customerId, 'credit-refund'],
    queryFn: () => paymentApi.customerPayments(invoice.customerId, { pageSize: 100 }),
    enabled: open,
  });

  // What may be SENT now, not what the memo says is owed — the invoice's own
  // "Credit remaining" is the descriptive figure and is shown elsewhere.
  const refundableNow = ledger.data?.creditRefundableNowCents ?? null;
  const currencyCode = invoice.currencyCode ?? companyCurrency;
  const money = (cents: number) => formatCents(cents, currencyCode, getDateLocale());
  const day = (value: string) => formatCompanyInstant(new Date(value), { dateStyle: 'medium' });

  // The customer's charges that could still give money back, each capped by
  // its own prior refunds — the same per-charge arithmetic the refund
  // endpoint applies, so nothing offered here is certain to be refused.
  const rows = customerPayments.data?.items ?? [];
  const entries = rows.map((row) => ({
    id: row.id,
    type: row.type,
    status: row.status,
    amountCents: row.amountCents,
    refundOfPaymentId: row.refundOfPaymentId,
  }));
  const refundableCharges = rows
    .filter(
      (row) =>
        row.type === 'charge' &&
        row.status === 'succeeded' &&
        row.providerAccountConnected &&
        row.currencyCode === currencyCode,
    )
    .map((row) => ({ payment: row, refundable: refundableCentsForCharge(row.id, entries) }))
    .filter((candidate) => candidate.refundable > 0);

  const refund = useMutation({
    mutationFn: (amountCents: number) => {
      const [kind, id] = target.split(':', 2);
      return paymentApi.refundCredit(invoice.id, {
        ...(kind === 'payment' ? { paymentId: id } : { methodId: id }),
        // Always a figure, never "whatever the server's cap is by now": the
        // cap can grow after this page read it (an earlier refund re-checked
        // as never sent), and the office agreed to the amount it was shown.
        amountCents,
        ...(note.trim() ? { reason: note.trim() } : {}),
        ...(kind === 'method' && date ? { receivedOn: date } : {}),
      });
    },
    onSuccess: (payment) => {
      setOpen(false);
      setNote('');
      setDate('');
      setAmount(null);
      setTarget('');
      onDone(
        // Cancelled belongs with failed: the processor said the money did not
        // go back, and it is reachable here because `refundPayment` reads back
        // a row a settlement webhook settled first. Not confirmed is neither —
        // the row is parked pending and the outcome is unknown — so only a
        // settled success is reported as money returned.
        payment.status === 'failed' || payment.status === 'cancelled'
          ? t('invoices.credit.refundFailed', { reason: payment.failureReason ?? '' })
          : payment.status === 'pending'
            ? t('invoices.credit.refundUnconfirmed', {
                reason: payment.failureReason ?? t('invoices.credit.refundUnconfirmedAccepted'),
              })
            : t('invoices.credit.refunded'),
      );
    },
    onError: (caught: unknown) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('invoices.list.actionFailed')),
  });

  if (invoice.status === 'void' || invoice.status === 'refunded') return null;

  const chosenCharge = target.startsWith('payment:')
    ? refundableCharges.find((candidate) => `payment:${candidate.payment.id}` === target)
    : undefined;
  // What the form may actually send back: what the memo can give back right
  // now, further capped by the chosen charge when one is chosen.
  const cap =
    refundableNow === null ? null : chosenCharge ? Math.min(refundableNow, chosenCharge.refundable) : refundableNow;
  // What a blank amount would send: the cap, when it is known and above zero.
  const blankGivesBack = cap !== null && cap > 0 ? cap : null;

  return (
    <div>
      {error ? <p className="mb-1 text-xs text-[color:var(--color-danger)]">{error}</p> : null}
      <Button
        variant="ghost"
        aria-expanded={open}
        onClick={() => {
          setError(null);
          setOpen((value) => !value);
        }}
      >
        {open ? t('invoices.credit.refundCancel') : t('invoices.credit.refundOpen')}
      </Button>
      {open ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            // A blank amount stands for the cap, so it can only be asked about
            // once the ledger has said what the cap is — and that there is
            // something to give back.
            const figure = amount ?? blankGivesBack;
            if (!target || figure === null) return;
            setConfirming(figure);
          }}
          className="mt-2 grid min-w-72 gap-2 rounded-md border border-[color:var(--color-line)] p-3"
        >
          {refundableNow !== null ? (
            <p className="text-sm">{t('invoices.credit.refundableNow', { amount: money(refundableNow) })}</p>
          ) : null}
          <Select
            label={t('invoices.credit.targetLabel')}
            value={target}
            onChange={(event) => {
              setTarget(event.target.value);
              setAmount(null);
            }}
            options={[
              { value: '', label: t('invoices.credit.targetPlaceholder') },
              ...refundableCharges.map((candidate) => ({
                value: `payment:${candidate.payment.id}`,
                label: t('invoices.credit.targetPayment', {
                  amount: money(candidate.payment.amountCents),
                  date: day(candidate.payment.receivedAt ?? candidate.payment.createdAt),
                  refundable: money(candidate.refundable),
                }),
              })),
              ...methods.map((method) => ({
                value: `method:${method.id}`,
                label: t('invoices.credit.targetMethod', {
                  method: localized.text(method.name, method.nameTranslations),
                }),
              })),
            ]}
          />
          <MoneyInput
            label={t('invoices.credit.amountLabel')}
            hint={cap !== null ? t('invoices.credit.amountHint', { amount: money(cap) }) : undefined}
            valueCents={amount}
            onChangeCents={setAmount}
            currencyCode={currencyCode}
          />
          {target.startsWith('method:') ? (
            <TextInput
              label={t('invoices.list.recordDateLabel')}
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          ) : null}
          <TextInput
            label={t('invoices.list.recordNoteLabel')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t('invoices.credit.notePlaceholder')}
            maxLength={200}
          />
          <Button
            type="submit"
            variant="primary"
            busy={refund.isPending}
            disabled={
              !target ||
              (amount === null && blankGivesBack === null) ||
              (amount !== null && (amount <= 0 || (cap !== null && amount > cap)))
            }
          >
            {t('invoices.credit.refundSubmit')}
          </Button>
        </form>
      ) : null}
      {confirming !== null ? (
        <ConfirmDialog
          title={t(target.startsWith('method:') ? 'singleJob.confirmRecordedRefund' : 'singleJob.confirmRefund', {
            amount: money(confirming),
          })}
          confirmLabel={t('invoices.credit.refundSubmit')}
          initialFocus="cancel"
          onConfirm={() => {
            setConfirming(null);
            refund.mutate(confirming);
          }}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * Writing an invoice by hand: a customer and a list of signed lines. The
 * examples this exists for come straight from the office — a late fee, the
 * bottle of polish they bought, the cleaning that happened but never got
 * scheduled — and, with the sign flipped, the credit owed back for the blinds
 * the crew broke. Lines typed as credits subtract; a form whose lines net
 * negative creates a credit memo, and says so before anything is submitted.
 */
function NewInvoiceComposer({
  companyCurrency,
  onCreated,
  onCancel,
}: {
  companyCurrency: string;
  onCreated: (message: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  interface LineDraft {
    description: string;
    quantity: string;
    amount: number | null;
    kind: 'charge' | 'credit';
  }
  const emptyLine: LineDraft = { description: '', quantity: '1', amount: null, kind: 'charge' };
  const [customerId, setCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine]);
  const [issuedOn, setIssuedOn] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Searched, not merely paged: a company can have far more customers than
  // one page, and a picker fed only the first hundred would make everyone
  // past it impossible to bill from here.
  const customers = useQuery({
    queryKey: ['customers', 'invoice-composer', customerSearch],
    queryFn: () => customerApi.list({ q: customerSearch || undefined, pageSize: 100 }),
    placeholderData: keepPreviousData,
  });

  const setLine = (index: number, patch: Partial<LineDraft>) =>
    setLines((current) => current.map((line, at) => (at === index ? { ...line, ...patch } : line)));

  // Only the lines complete enough to mean something count toward the total —
  // the same lines the submit sends.
  const completeLines = lines
    .filter((line) => line.description.trim() && line.amount !== null && line.amount > 0)
    .map((line) => ({
      description: line.description.trim(),
      quantity: Math.max(1, Number.parseInt(line.quantity, 10) || 1),
      unitCents: line.kind === 'credit' ? -(line.amount as number) : (line.amount as number),
    }));
  const totalCents = lineItemsTotalCents(completeLines);
  // Lines somebody started typing. Any of them not complete is left off the
  // invoice, so Enter in a half-typed line (which submits the form) and
  // Cancel both have to ask rather than lose it quietly.
  const startedLines = lines.filter((line) => line.description.trim() || line.amount !== null).length;
  const incompleteLines = startedLines - completeLines.length;

  const create = useMutation({
    mutationFn: () =>
      invoiceApi.create({
        customerId,
        lineItems: completeLines,
        ...(issuedOn ? { issuedOn } : {}),
      }),
    onSuccess: (detail) =>
      onCreated(
        (detail.totalCents ?? 0) < 0
          ? t('invoices.new.createdCredit', { number: detail.invoiceNumber })
          : t('invoices.new.created', { number: detail.invoiceNumber }),
      ),
    onError: (caught: unknown) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('invoices.list.actionFailed')),
  });

  return (
    <form
      className="card mb-6 grid gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        if (!customerId || completeLines.length === 0 || totalCents === 0) return;
        if (
          incompleteLines > 0 &&
          !window.confirm(t('invoices.new.confirmDropIncomplete', { count: incompleteLines }))
        ) {
          return;
        }
        create.mutate();
      }}
    >
      <h2 className="text-base font-semibold">{t('invoices.new.heading')}</h2>
      {error ? <Alert tone="error">{error}</Alert> : null}
      <div className="grid gap-3 sm:grid-cols-3">
        {/*
          Searching no longer clears the pick. It used to have to: the picker
          only ever showed the current results, so a customer chosen before the
          next search would vanish off it while the id stayed set, and the
          invoice went out to somebody whose name was nowhere on the form. The
          combined control keeps showing whoever is chosen whatever the list
          currently holds, so the choice can safely outlive the search that
          found them.
        */}
        <SearchSelect
          label={t('invoices.new.customerLabel')}
          hint={t('searchSelect.hint')}
          placeholder={t('invoices.new.customerPlaceholder')}
          value={customerId}
          onChange={setCustomerId}
          onSearch={setCustomerSearch}
          loading={customers.isFetching}
          errored={customers.isError}
          options={(customers.data?.items ?? []).map((customer) => ({
            value: customer.id,
            label: customer.displayName,
          }))}
        />
        <TextInput
          label={t('invoices.new.issuedOnLabel')}
          type="date"
          value={issuedOn}
          onChange={(event) => setIssuedOn(event.target.value)}
          hint={t('invoices.new.issuedOnHint')}
        />
      </div>

      <div className="grid gap-2">
        {lines.map((line, index) => (
          <div
            key={index}
            className="grid items-end gap-2 rounded-md border border-[color:var(--color-line)] p-3 sm:grid-cols-[1fr_6rem_8rem_9rem_auto]"
          >
            <TextInput
              label={t('invoices.new.lineDescriptionLabel')}
              value={line.description}
              onChange={(event) => setLine(index, { description: event.target.value })}
              placeholder={t('invoices.new.lineDescriptionPlaceholder')}
              maxLength={200}
            />
            <TextInput
              label={t('invoices.new.lineQuantityLabel')}
              type="number"
              min={1}
              max={9999}
              value={line.quantity}
              onChange={(event) => setLine(index, { quantity: event.target.value })}
            />
            <MoneyInput
              label={t('invoices.new.lineUnitLabel')}
              valueCents={line.amount}
              onChangeCents={(cents) => setLine(index, { amount: cents })}
              currencyCode={companyCurrency}
            />
            {/* A credit is a choice about the line, not a minus sign to
                remember: "Charge" adds, "Credit" subtracts, and the total
                below says what the mix comes to. */}
            <Select
              label={t('invoices.new.lineKindLabel')}
              value={line.kind}
              onChange={(event) => setLine(index, { kind: event.target.value as LineDraft['kind'] })}
              options={[
                { value: 'charge', label: t('invoices.new.lineKindCharge') },
                { value: 'credit', label: t('invoices.new.lineKindCredit') },
              ]}
            />
            <Button
              variant="ghost"
              disabled={lines.length === 1}
              onClick={() => setLines((current) => current.filter((_, at) => at !== index))}
            >
              {t('invoices.new.lineRemove')}
            </Button>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          variant="ghost"
          disabled={lines.length >= MAX_INVOICE_LINE_ITEMS}
          onClick={() => setLines((current) => [...current, emptyLine])}
        >
          {t('invoices.new.addLine')}
        </Button>
        <p className="text-sm font-semibold">
          {t('invoices.new.total', { amount: formatCents(totalCents, companyCurrency, getDateLocale()) })}
          {totalCents < 0 ? (
            <span className="ml-2 font-normal text-[color:var(--color-muted)]">{t('invoices.new.creditNote')}</span>
          ) : null}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          variant="primary"
          busy={create.isPending}
          disabled={!customerId || completeLines.length === 0 || totalCents === 0}
        >
          {totalCents < 0 ? t('invoices.new.submitCredit') : t('invoices.new.submit')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            if (startedLines > 0 && !window.confirm(t('invoices.new.confirmDiscard', { count: startedLines }))) return;
            onCancel();
          }}
        >
          {t('invoices.new.cancel')}
        </Button>
      </div>
    </form>
  );
}

/**
 * Whether this row may offer to send its invoice at all.
 *
 * Only the two states the row can be sure of on its own: a void invoice or
 * canceled visit is not owed for, and a cleaning that has not happened yet has
 * no invoice to send — the message says "your recent cleaning", and a row exists
 * up to 84 days before the work does. Everything else the endpoint refuses with
 * a reason, which the page reports rather than pre-empting; in particular a
 * PART-paid invoice reads as `paid` here (any collected money does), so refusing
 * on that status would hide the send most likely to be wanted.
 *
 * One predicate for both the row's own button and its selection box, so a bulk
 * send can never gather a row that could not have been sent by hand.
 */
function canEmailInvoice(invoice: Invoice): boolean {
  if (invoice.status === 'void' || invoice.appointmentStatus === 'cancelled') return false;
  // A manual invoice has no work to wait for — it can go the moment it is
  // written. A credit memo asks the customer for nothing, so there is nothing
  // to send; the PDF is the document to share.
  if (invoice.kind === 'manual') return (invoice.totalCents ?? 0) > 0;
  return invoice.startsAt !== null && new Date(invoice.startsAt).getTime() <= Date.now();
}

/** A manual invoice as a credit memo: money the company owes the customer back. */
function isCreditMemo(invoice: Invoice): boolean {
  return invoice.kind === 'manual' && (invoice.totalCents ?? 0) < 0;
}

/**
 * A quiet tag beside the number of a hand-written invoice, so the ledger's
 * mixed list says which rows have no visit behind them — and which of those
 * owe money OUT rather than in.
 */
function InvoiceKindTag({ invoice }: { invoice: Invoice }) {
  const { t } = useTranslation();
  if (invoice.kind !== 'manual') return null;
  return (
    <span className={classes('badge', TONES.not_billed)}>
      {isCreditMemo(invoice) ? t('invoices.list.kindCredit') : t('invoices.list.kindManual')}
    </span>
  );
}

/**
 * Sending the customer their invoice by hand, from its own row.
 *
 * The app already emails each invoice once, automatically, when its cleaning
 * starts — but that send happens once and is never repeated, so an email that
 * is filtered, deleted, or sent to an address the customer no longer reads
 * leaves the office with nothing to do but ring them. This is the button for
 * that: the same invoice, the same durable payment link, sent again now.
 *
 * The address box is the other half of "they never got it" — a mistyped
 * address, or a landlord who pays for a tenant's cleaning. It addresses this
 * one message and does not touch the customer record, which is why it is left
 * blank rather than pre-filled: an empty box that says "address on file" cannot
 * be edited by accident into a permanent-looking change it will not make.
 *
 * Shown wherever `canEmailInvoice` allows; everything else the endpoint can
 * refuse — an archived location, a payment in flight, a balance settled since
 * the page loaded — is a message the office reads under the button.
 */
function InvoiceEmailAction({ invoice, onSent }: { invoice: Invoice; onSent: (message: string | null) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [error, setError] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () => invoiceApi.send(invoice.id, recipient.trim() ? { recipientEmail: recipient.trim() } : {}),
    onSuccess: (result) => {
      // A send that reached nobody is not a success, however the request went:
      // a suppressed or bounced address answers 200 saying so, and telling the
      // office "emailed" would send them away from the thing still broken.
      if (!result.delivered) {
        setError(t('invoices.list.emailNotDelivered', { recipient: result.emailedTo }));
        return;
      }
      setOpen(false);
      setRecipient('');
      onSent(t('invoices.list.emailSent', { recipient: result.emailedTo }));
    },
    onError: (caught: unknown) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('invoices.list.actionFailed')),
  });

  if (!canEmailInvoice(invoice)) return null;

  return (
    <div className="mt-2">
      <Button
        variant="ghost"
        aria-expanded={open}
        onClick={() => {
          setError(null);
          // The page-level notice belongs to whatever was last sent, from any
          // row. Left up, a refusal here would sit under somebody else's
          // "Invoice emailed" and read as if this one had landed too.
          onSent(null);
          setOpen((value) => !value);
        }}
      >
        {open ? t('invoices.list.emailCancel') : t('invoices.list.emailInvoice')}
      </Button>
      {open ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            onSent(null);
            send.mutate();
          }}
          className="mt-2 grid min-w-72 gap-2 rounded-md border border-[color:var(--color-line)] p-3"
        >
          {error ? <p className="text-xs text-[color:var(--color-danger)]">{error}</p> : null}
          <TextInput
            label={t('invoices.list.emailRecipientLabel')}
            type="email"
            value={recipient}
            onChange={(event) => setRecipient(event.target.value)}
            placeholder={t('invoices.list.emailRecipientPlaceholder')}
            hint={t('invoices.list.emailRecipientHint')}
            maxLength={320}
          />
          <Button type="submit" variant="primary" busy={send.isPending}>
            {t('invoices.list.emailSubmit')}
          </Button>
        </form>
      ) : null}
    </div>
  );
}

/**
 * Sending every invoice the office has ticked, and saying what became of each.
 *
 * The case this exists for is a run of cleanings whose invoices never went out
 * — a spell with the automatic mail switched off, a provider outage, a batch
 * that aged past the seven-day window the cron works inside. One at a time that
 * is the same decision twelve times over.
 *
 * The summary is the whole point of the bulk send, and it is deliberately not a
 * count on its own. Twelve invoices are not in twelve identical states — one
 * was paid in cash this morning, one visit was canceled, one customer has no
 * address — so the endpoint reports each separately and this names the ones
 * that did not go, by invoice number, with the reason. A bare "10 of 12 sent"
 * would leave somebody to work out which two.
 */
function BulkEmailResult({ result }: { result: InvoiceBatchSendResult }) {
  const { t } = useTranslation();
  const problems = result.results.filter((row) => row.status !== 'sent');
  const reason = (row: InvoiceBatchSendResult['results'][number]) => {
    if (row.status === 'not_found') return t('invoices.list.bulkReason.notFound');
    if (row.status === 'undelivered') return t('invoices.list.bulkReason.undelivered', { recipient: row.emailedTo });
    return t(`invoices.list.bulkReason.${row.refusal ?? 'unknown'}`);
  };

  return (
    <Alert
      tone={problems.length === 0 ? 'success' : 'warning'}
      title={t('invoices.list.bulkSent', { count: result.sent })}
    >
      {problems.length === 0 ? null : (
        <>
          <p className="mb-1">{t('invoices.list.bulkNotSent', { count: problems.length })}</p>
          <ul className="list-disc pl-5">
            {problems.map((row) => (
              <li key={row.invoiceId}>
                {row.invoiceNumber ?? t('invoices.list.bulkUnknownInvoice')} — {reason(row)}
              </li>
            ))}
          </ul>
        </>
      )}
    </Alert>
  );
}

/**
 * What an invoice is made of, opened from its own row: the visit being billed,
 * who it is billed to, and — the part the list cannot show — every payment
 * taken against it, with what that leaves owing.
 *
 * The ledger is fetched per invoice rather than joined into the list: a page
 * of 25 invoices would otherwise carry every payment row for 25 visits so that
 * one of them could be expanded.
 */
function InvoiceDetailPanel({
  invoiceId,
  columns,
  canWrite,
  canRefund,
  configured,
  tipsEnabled,
  methods,
  companyCurrency,
  onChanged,
}: {
  invoiceId: string;
  columns: number;
  canWrite: boolean;
  canRefund: boolean;
  configured: boolean;
  tipsEnabled: boolean;
  methods: CompanyPaymentMethod[];
  companyCurrency: string;
  onChanged: (message: string) => void;
}) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: ({ signal }) => invoiceApi.get(invoiceId, signal),
  });

  const detail = query.data;
  const manual = detail?.kind === 'manual';
  const money = (cents: number, currencyCode: string) => formatCents(cents, currencyCode, getDateLocale());
  const day = (value: string) => formatCompanyInstant(new Date(value), { dateStyle: 'medium' });

  const [voidError, setVoidError] = useState<string | null>(null);
  const voidInvoice = useMutation({
    mutationFn: () => invoiceApi.void(invoiceId),
    onSuccess: () => onChanged(t('invoices.detail.voided')),
    onError: (caught: unknown) =>
      setVoidError(caught instanceof ApiRequestError ? caught.message : t('invoices.list.actionFailed')),
  });

  /**
   * The way out of a refund the processor never confirmed, on the screen where
   * a CREDIT MEMO's refunds actually live.
   *
   * A refund filed against a credit memo (`POST /payments/invoices/:id/refund`)
   * belongs to no visit, so the appointment panel that carries this button
   * never shows it. Without the same action here the memo's reserved amount
   * could only be released by someone editing the database — the very dead end
   * the re-check exists to remove.
   */
  const [recheckingId, setRecheckingId] = useState<string | null>(null);
  const recheck = useMutation({
    mutationFn: (paymentId: string) => {
      setRecheckingId(paymentId);
      return paymentApi.recheckRefund(paymentId);
    },
    onSuccess: (payment) => {
      setRecheckingId(null);
      void query.refetch();
      onChanged(
        payment.status === 'succeeded'
          ? t('appointmentPayments.refundIssued')
          : payment.status === 'pending'
            ? // The provider sometimes says WHY it is unresolved — "this refund
              // needs confirming", which no amount of re-checking will clear by
              // itself. The lookup stores that reason precisely so somebody can
              // act on it, and the generic sentence threw it away.
              payment.failureReason
              ? t('appointmentPayments.refundStillUnconfirmedReason', { reason: payment.failureReason })
              : t('appointmentPayments.refundStillUnconfirmed')
            : t('appointmentPayments.refundNotSent', { reason: payment.failureReason ?? '' }),
      );
    },
    onError: (caught: unknown) => {
      setRecheckingId(null);
      setVoidError(caught instanceof ApiRequestError ? caught.message : t('invoices.list.actionFailed'));
    },
  });

  function rowLabel(payment: Payment): string {
    if (payment.type === 'refund') return t('invoices.detail.refundLabel');
    if (payment.method === 'manual') return payment.manualMethodName ?? t('invoices.detail.recordedLabel');
    if (payment.method === 'tap_to_pay') return t('invoices.detail.tapToPayLabel');
    return t('invoices.detail.chargeLabel');
  }

  return (
    <tr className="border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)] last:border-0">
      {/* Bounded: the row spans the ledger, so without a cap its prose
          would set the table's content width the moment it opens. */}
      <td colSpan={columns} className="max-w-[56rem] px-4 py-4">
        {query.isLoading ? <LoadingBlock label={t('invoices.detail.loading')} /> : null}

        {query.isError ? <Alert tone="error">{(query.error as Error).message}</Alert> : null}

        {detail ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-[color:var(--color-muted)]">{t('invoices.detail.issued')}</dt>
              <dd>{day(detail.issuedAt)}</dd>
              {/* The customer's own purchase-order number, snapshotted at
                  issue — the reference their accounts office files this under. */}
              {detail.poNumber ? (
                <>
                  <dt className="text-[color:var(--color-muted)]">{t('invoices.detail.poNumber')}</dt>
                  <dd>{detail.poNumber}</dd>
                </>
              ) : null}
              {/* A manual invoice has no visit: its lines, below, are the work. */}
              {detail.startsAt !== null && detail.startTimeLocal !== null && detail.durationMinutes !== null ? (
                <>
                  <dt className="text-[color:var(--color-muted)]">{t('invoices.detail.visit')}</dt>
                  <dd>
                    {day(detail.startsAt)} · {formatLocalTime(detail.startTimeLocal)} ·{' '}
                    {formatMinutes(detail.durationMinutes)}
                  </dd>
                </>
              ) : null}
              {detail.appointmentStatus !== null ? (
                <>
                  <dt className="text-[color:var(--color-muted)]">{t('invoices.detail.visitStatus')}</dt>
                  <dd>{t(`schedule.appointmentStatus.${detail.appointmentStatus}`)}</dd>
                </>
              ) : null}
              <dt className="text-[color:var(--color-muted)]">{t('invoices.detail.customer')}</dt>
              <dd>
                <div>{detail.customerName}</div>
                {detail.customerEmail ? (
                  <div className="text-[color:var(--color-muted)]">{detail.customerEmail}</div>
                ) : null}
                {detail.customerPhone ? (
                  <div className="text-[color:var(--color-muted)]">{detail.customerPhone}</div>
                ) : null}
              </dd>
              {detail.propertyAddress ? (
                <>
                  <dt className="text-[color:var(--color-muted)]">{t('invoices.detail.property')}</dt>
                  <dd className="whitespace-pre-line">
                    <AddressLink address={detail.propertyAddress}>{detail.propertyAddress}</AddressLink>
                  </dd>
                </>
              ) : null}
              <dt className="text-[color:var(--color-muted)]">{t('invoices.detail.amount')}</dt>
              <dd className="tabular">
                {manual
                  ? money(detail.totalCents ?? 0, detail.totals.currencyCode)
                  : detail.priceCents === null
                    ? t('invoices.list.priceNotSet')
                    : money(detail.priceCents, detail.totals.currencyCode)}
              </dd>
              {isCreditMemo(detail) ? (
                <>
                  {detail.totals.refundedCents > 0 ? (
                    <>
                      <dt className="text-[color:var(--color-muted)]">{t('invoices.detail.givenBack')}</dt>
                      <dd className="tabular">{money(detail.totals.refundedCents, detail.totals.currencyCode)}</dd>
                    </>
                  ) : null}
                  <dt className="font-semibold">{t('invoices.detail.creditRemaining')}</dt>
                  <dd className="tabular font-semibold">
                    {detail.status === 'void'
                      ? t('invoices.detail.voidNotice')
                      : money(detail.totals.creditRemainingCents, detail.totals.currencyCode)}
                  </dd>
                </>
              ) : (
                <>
                  <dt className="text-[color:var(--color-muted)]">{t('invoices.detail.paid')}</dt>
                  <dd className="tabular">{money(detail.totals.paidCents, detail.totals.currencyCode)}</dd>
                  {detail.totals.refundedCents > 0 ? (
                    <>
                      <dt className="text-[color:var(--color-muted)]">{t('invoices.detail.refunded')}</dt>
                      <dd className="tabular">{money(detail.totals.refundedCents, detail.totals.currencyCode)}</dd>
                    </>
                  ) : null}
                  <dt className="font-semibold">
                    {detail.status === 'void' ? t('invoices.detail.balance') : t('invoices.detail.balanceDue')}
                  </dt>
                  <dd className="tabular font-semibold">
                    {detail.status === 'void'
                      ? t('invoices.detail.voidNotice')
                      : money(detail.totals.balanceCents, detail.totals.currencyCode)}
                  </dd>
                </>
              )}
            </dl>

            <div>
              {detail.visitLines?.length ? (
                <>
                  <h3 className="mb-2 text-sm font-semibold">{t('invoices.detail.lineItemsHeading')}</h3>
                  <ul
                    className="mb-4 divide-y divide-[color:var(--color-line)] text-sm"
                    aria-label={t('invoices.detail.lineItemsHeading')}
                  >
                    {detail.visitLines.map((line, index) => (
                      <li key={index} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5">
                        <span>{line.label ?? t('invoices.detail.visitLine')}</span>
                        <span className="tabular font-medium">
                          {money(line.amountCents, detail.totals.currencyCode)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {detail.lineItems.length > 0 ? (
                <>
                  <h3 className="mb-2 text-sm font-semibold">{t('invoices.detail.lineItemsHeading')}</h3>
                  <ul className="mb-4 divide-y divide-[color:var(--color-line)] text-sm">
                    {detail.lineItems.map((line) => (
                      <li key={line.id} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5">
                        <span>
                          {line.quantity > 1 ? `${line.quantity} × ` : ''}
                          {line.description}
                          {line.quantity > 1 ? (
                            <span className="ml-2 text-[color:var(--color-muted)]">
                              {t('invoices.detail.lineUnit', {
                                amount: money(line.unitCents, detail.totals.currencyCode),
                              })}
                            </span>
                          ) : null}
                        </span>
                        <span className="tabular font-medium">
                          {money(line.amountCents, detail.totals.currencyCode)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}

              {/* The one place the true balance is known, so the one place a
                  partly-paid manual invoice (whose badge already reads paid)
                  can still be charged or recorded against. */}
              {canWrite && manual && !isCreditMemo(detail) ? (
                <div className="mb-4">
                  <InvoicePayActions
                    invoice={detail}
                    configured={configured}
                    tipsEnabled={tipsEnabled}
                    methods={methods}
                    companyCurrency={companyCurrency}
                    onDone={onChanged}
                    balanceCents={detail.totals.balanceCents}
                  />
                </div>
              ) : null}

              {voidError ? <p className="mb-2 text-xs text-[color:var(--color-danger)]">{voidError}</p> : null}
              {/* Only while the guarded void could actually succeed: a ledger
                  holding any pending or succeeded row makes it a button whose
                  one outcome is a conflict. */}
              {canWrite &&
              manual &&
              detail.status !== 'void' &&
              detail.payments.every((payment) => payment.status !== 'pending' && payment.status !== 'succeeded') ? (
                <div className="mb-4">
                  <Button
                    variant="ghost"
                    busy={voidInvoice.isPending}
                    onClick={() => {
                      setVoidError(null);
                      if (window.confirm(t('invoices.detail.confirmVoid', { number: detail.invoiceNumber }))) {
                        voidInvoice.mutate();
                      }
                    }}
                  >
                    {t('invoices.detail.voidAction')}
                  </Button>
                </div>
              ) : null}

              <h3 className="mb-2 text-sm font-semibold">{t('invoices.detail.paymentsHeading')}</h3>
              {detail.payments.length === 0 ? (
                <p className="text-sm text-[color:var(--color-muted)]">{t('invoices.detail.noPayments')}</p>
              ) : (
                <ul className="divide-y divide-[color:var(--color-line)] text-sm">
                  {detail.payments.map((payment) => (
                    <li key={payment.id} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5">
                      <span>
                        {rowLabel(payment)}
                        <span className="ml-2 text-[color:var(--color-muted)]">
                          {day(payment.receivedAt ?? payment.createdAt)}
                        </span>
                        <span className="ml-2 text-[color:var(--color-muted)]">
                          {t(`payments.status.${payment.status}`)}
                          {payment.failureReason ? ` — ${payment.failureReason}` : ''}
                        </span>
                        {payment.tipCents > 0 ? (
                          <span className="ml-2 text-[color:var(--color-muted)]">
                            {/* The amount beside this row is tip-inclusive
                                while the balance above is not — the tip has
                                to explain the difference. */}
                            {t('appointmentPayments.tipIncluded', {
                              tip: money(payment.tipCents, payment.currencyCode),
                            })}
                          </span>
                        ) : null}
                      </span>
                      <span className="flex items-baseline gap-2">
                        {/* A refund nobody has heard back about is the one row
                            here the office can still do something about. */}
                        {payment.type === 'refund' && payment.status === 'pending' && canRefund ? (
                          <Button
                            variant="ghost"
                            busy={recheckingId === payment.id}
                            onClick={() => recheck.mutate(payment.id)}
                          >
                            {t('appointmentPayments.refundRecheck')}
                          </Button>
                        ) : null}
                        <span className="tabular font-medium">
                          {/* Each row in its own currency: a refund taken in
                              euros is not a dollar figure, and neither is a
                              charge on a visit priced before the company moved
                              currencies. */}
                          {payment.type === 'refund' ? '−' : ''}
                          {money(payment.amountCents, payment.currencyCode)}
                        </span>
                        {payment.received ? (
                          <span className="text-xs text-[color:var(--color-muted)]">
                            {t('invoices.list.receivedAs', {
                              amount: money(payment.received.amountCents, payment.received.currencyCode),
                              rate: payment.received.exchangeRate,
                            })}
                          </span>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * One monthly invoice's own visits, opened from its row — where its money
 * actually settles, since a consolidated invoice has no ledger of its own.
 */
function MonthlyInvoiceDetailPanel({ invoiceId, columns }: { invoiceId: string; columns: number }) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ['invoice-monthly', invoiceId],
    queryFn: ({ signal }) => invoiceApi.monthlyGet(invoiceId, signal),
  });
  const detail = query.data;

  return (
    <tr className="border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)] last:border-0">
      {/* Bounded: the row spans the ledger, so without a cap its prose
          would set the table's content width the moment it opens. */}
      <td colSpan={columns} className="max-w-[56rem] px-4 py-4">
        {query.isLoading ? <LoadingBlock label={t('invoices.detail.loading')} /> : null}
        {query.isError ? <Alert tone="error">{(query.error as Error).message}</Alert> : null}
        {detail ? (
          <div>
            <h3 className="mb-2 text-sm font-semibold">{t('invoices.monthly.visitsHeading')}</h3>
            {detail.visits.length === 0 ? (
              <p className="text-sm text-[color:var(--color-muted)]">{t('invoices.monthly.noVisits')}</p>
            ) : (
              <ul className="divide-y divide-[color:var(--color-line)] text-sm">
                {detail.visits.map((visit) => (
                  <li key={visit.appointmentId} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5">
                    <span>
                      {formatShortDate(visit.scheduledDate)}
                      {visit.propertyAddress ? (
                        <span className="ml-2 text-[color:var(--color-muted)]">
                          <AddressLink address={visit.propertyAddress}>
                            {visit.propertyAddress.split('\n')[0]}
                          </AddressLink>
                        </span>
                      ) : null}
                      <span className="ml-2 text-[color:var(--color-muted)]">
                        {t(`payments.appointmentStatus.${visit.paymentStatus}`)}
                      </span>
                    </span>
                    <span className="tabular font-medium">
                      {visit.priceCents === null
                        ? t('invoices.list.priceNotSet')
                        : formatCents(visit.priceCents, visit.currencyCode, getDateLocale())}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/*
              The extras the same document charged for. The total already
              included them, so leaving them out meant a client's
              accounts-payable department could not make the invoice add up —
              and an extras-only month read as "no visits" beside a non-zero
              figure.
            */}
            {detail.changeOrders.length > 0 ? (
              <>
                <h3 className="mt-4 mb-2 text-sm font-semibold">{t('invoices.monthly.changeOrdersHeading')}</h3>
                <ul className="divide-y divide-[color:var(--color-line)] text-sm">
                  {detail.changeOrders.map((order) => (
                    <li key={order.id} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5">
                      <span>
                        {order.title}
                        <span className="ml-2 text-[color:var(--color-muted)]">{order.number}</span>
                        {order.approvedByName ? (
                          <span className="ml-2 text-[color:var(--color-muted)]">
                            {t('invoices.monthly.approvedBy', { name: order.approvedByName })}
                          </span>
                        ) : null}
                      </span>
                      <span className="tabular font-medium">
                        {formatCents(order.amountCents, order.currencyCode, getDateLocale())}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {detail.supplies.length > 0 ? (
              <>
                <h3 className="mt-4 mb-2 text-sm font-semibold">{t('invoices.monthly.suppliesHeading')}</h3>
                <ul className="divide-y divide-[color:var(--color-line)] text-sm">
                  {detail.supplies.map((restock) => (
                    <li key={restock.id} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5">
                      <span>
                        {t('invoices.monthly.supplyLine', { quantity: restock.quantity, item: restock.itemName })}
                        {restock.siteName ? (
                          <span className="ml-2 text-[color:var(--color-muted)]">{restock.siteName}</span>
                        ) : null}
                      </span>
                      <span className="tabular font-medium">
                        {formatCents(restock.amountCents, restock.currencyCode, getDateLocale())}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {/*
              Said once, at the bottom: on a tax-enabled company the extras
              above carry tax the visit prices already included, so the lines
              and the invoice figure differ by design rather than by error.
            */}
            {detail.changeOrders.length > 0 || detail.supplies.length > 0 ? (
              <p className="mt-3 text-xs text-[color:var(--color-muted)]">{t('invoices.monthly.extrasTaxNote')}</p>
            ) : null}
          </div>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * The consolidated monthly invoices — one document per customer per month for
 * customers on consolidated billing. Its own tab rather than rows in the
 * visit list, because the two are different animals: a monthly invoice has a
 * period and a snapshotted total where a visit invoice has a visit.
 */
function MonthlyInvoicesSection() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: ['invoices-monthly', page],
    queryFn: () => invoiceApi.monthlyList(page),
    placeholderData: (previous) => previous,
  });

  const money = (invoice: MonthlyInvoice) => formatCents(invoice.totalCents, invoice.currencyCode, getDateLocale());
  const items = query.data?.items ?? [];
  const COLUMNS = 8;

  if (query.isLoading) return <LoadingBlock label={t('invoices.list.loading')} />;

  if (query.isError) {
    return (
      <Alert tone="error" title={t('invoices.list.loadErrorTitle')}>
        {(query.error as Error).message}
      </Alert>
    );
  }

  if (items.length === 0) {
    return (
      <WorkEmpty title={t('invoices.monthly.emptyTitle')}>
        <p>{t('invoices.monthly.emptyMessage')}</p>
      </WorkEmpty>
    );
  }

  return (
    <TableScroll
      aria-label={t('invoices.monthly.caption', { count: items.length })}
      className="overflow-x-auto rounded-lg border border-[color:var(--color-line)] bg-white"
    >
      <table className="data-table w-full min-w-max border-collapse text-left">
        <caption className="sr-only">{t('invoices.monthly.caption', { count: items.length })}</caption>
        <thead>
          <tr className="border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)]">
            <th scope="col" className="px-4 py-3 text-sm font-semibold">
              {t('invoices.list.columnInvoice')}
            </th>
            <th scope="col" className="px-4 py-3 text-sm font-semibold">
              {t('invoices.list.columnCustomer')}
            </th>
            <th scope="col" className="px-4 py-3 text-sm font-semibold">
              {t('invoices.monthly.columnPeriod')}
            </th>
            <th scope="col" className="px-4 py-3 text-sm font-semibold">
              {t('invoices.list.columnStatus')}
            </th>
            <th scope="col" className="px-4 py-3 text-sm font-semibold">
              {t('invoices.list.columnDue')}
            </th>
            <th scope="col" className="px-4 py-3 text-right text-sm font-semibold">
              {t('invoices.list.columnAmount')}
            </th>
            <th scope="col" className="px-4 py-3 text-right text-sm font-semibold">
              {t('invoices.monthly.columnVisitsPaid')}
            </th>
            <th scope="col" className="px-4 py-3 text-sm font-semibold">
              <span className="sr-only">{t('invoices.list.columnDocument')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((invoice) => (
            <Fragment key={invoice.id}>
              <tr className="border-b border-[color:var(--color-line)] align-top last:border-0">
                <td className="px-4 py-3">
                  <div className="font-semibold">{invoice.invoiceNumber}</div>
                  <div className="text-sm text-[color:var(--color-muted)]">
                    {formatNumericInstant(invoice.issuedAt)}
                  </div>
                </td>
                <td className="max-w-[18rem] table-text px-4 py-3">{invoice.customerName}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  {t('invoices.monthly.period', {
                    from: formatShortDate(invoice.periodStart),
                    to: formatShortDate(invoice.periodEnd),
                  })}
                </td>
                <td className="px-4 py-3">
                  <InvoiceStatusBadge status={invoice.status} />
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  {invoice.dueOn ? formatShortDate(invoice.dueOn) : '—'}
                </td>
                <td className="tabular px-4 py-3 text-right font-semibold">{money(invoice)}</td>
                {/* Payment settles per visit, so "how far along is the month"
                    is a count of visits paid, not a single status. */}
                <td className="tabular px-4 py-3 text-right text-sm">
                  {t('invoices.monthly.visitsPaid', { paid: invoice.paidVisitCount, total: invoice.visitCount })}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    variant="ghost"
                    aria-expanded={expanded === invoice.id}
                    onClick={() => setExpanded((current) => (current === invoice.id ? null : invoice.id))}
                  >
                    {expanded === invoice.id ? t('invoices.list.hideDetails') : t('invoices.list.details')}
                  </Button>
                </td>
              </tr>
              {expanded === invoice.id ? <MonthlyInvoiceDetailPanel invoiceId={invoice.id} columns={COLUMNS} /> : null}
            </Fragment>
          ))}
        </tbody>
      </table>
      <Pagination page={page} totalPages={query.data?.totalPages} onPageChange={setPage} />
    </TableScroll>
  );
}

/**
 * The plan decides whether this page exists for a company at all. The check
 * is a wrapper rather than an early return inside the page, so the body's
 * hooks keep running in the same order on every render.
 */
export default function InvoicesPage() {
  const { t } = useTranslation();
  if (!usePlanFeature('invoicing')) return <PlanRequiredPage feature="invoicing" title={t('invoices.list.title')} />;
  return <InvoicesPageContent />;
}

type InvoicesTab = 'visits' | 'upcoming' | 'monthly';

function InvoicesPageContent() {
  const { t } = useTranslation();
  useDocumentTitle(t('invoices.list.documentTitle'));
  const { company, can, currencyCode: locationCurrency } = useSession();
  const queryClient = useQueryClient();

  const STATUS_OPTIONS = [
    { value: '', label: t('invoices.list.statusAll') },
    ...INVOICE_STATUSES.map((status) => ({
      value: status,
      label: status === 'void' ? t('invoices.list.statusVoid') : t(`payments.appointmentStatus.${status}`),
    })),
  ];

  const SORT_OPTIONS = [
    { value: 'issued_desc', label: t('invoices.list.sortIssuedDesc') },
    { value: 'issued_asc', label: t('invoices.list.sortIssuedAsc') },
    { value: 'number_desc', label: t('invoices.list.sortNumberDesc') },
    { value: 'number_asc', label: t('invoices.list.sortNumberAsc') },
    { value: 'scheduled_desc', label: t('invoices.list.sortScheduledDesc') },
    { value: 'scheduled_asc', label: t('invoices.list.sortScheduledAsc') },
  ];

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  /** The office's own sort, or '' for the tab's: latest visit first, soonest first on Upcoming. */
  const [sortPick, setSortPick] = useState('');
  const [page, setPage] = useState(1);
  /**
   * Which list is showing: the invoice-per-visit ledger through the end of this
   * week, the visits after it, or the consolidated monthly one. The ledger
   * opens on what is due now, not on the far end of a recurring schedule.
   */
  const [tab, setTab] = useState<InvoicesTab>('visits');
  /**
   * The monthly tab is commercial work's tab. Consolidated billing is a
   * customer setting a residential company is never offered (customer-form
   * hides it), so for a housecleaner that list is empty — and an empty tab
   * beside the ledger reads as a feature that is broken rather than one that
   * does not apply.
   *
   * Empty is not assumed, though, it is ASKED. A monthly invoice does not
   * stop existing when a company switches its mode: a customer already on
   * consolidated billing keeps the setting, the consolidation cron keeps
   * gathering their month, and every one of those documents is invisible in
   * the visit ledger. Hiding the tab on the mode alone would put a live
   * receivable somewhere nobody in the office can reach. So the probe runs
   * for a residential company — one page-1 request, sharing MonthlyInvoices-
   * Section's own cache key, costing nothing when the tab then opens — and
   * the tab stays wherever there is a document behind it.
   *
   * `visits` is the default, so nothing has to move when a company switches
   * mode with the page open.
   */
  const commercialWork = useCommercialWork();
  const monthlyProbe = useQuery({
    queryKey: ['invoices-monthly', 1],
    queryFn: () => invoiceApi.monthlyList(1),
    enabled: !commercialWork,
  });
  /**
   * A probe that FAILED is not a probe that answered zero. Reading an
   * unreachable endpoint as "no monthly invoices" would take a company's
   * outstanding receivables off the screen on a flaky minute and give them
   * nowhere to see the error either — so the tab stays and the section shows
   * its own error, which is the ordinary treatment every other list gets.
   *
   * A probe still in flight reads as none, on purpose and unlike a failed one.
   * The answer is no for nearly every company that asks — a residential
   * company is not on consolidated billing — so showing the tab first and
   * removing it a moment later would put a flicker on every invoices page in
   * the app to spare one rare company a short wait. Late arrival is the quiet
   * direction; the tab appears when the answer does.
   */
  const tabs: ReadonlyArray<InvoicesTab> =
    commercialWork || monthlyProbe.isError || (monthlyProbe.data?.total ?? 0) > 0
      ? ['visits', 'upcoming', 'monthly']
      : ['visits', 'upcoming'];
  /**
   * Which list is actually showing — derived from the tabs that exist, for the
   * reason the reports page derives its section: `tab` is a pick, and a pick
   * outlives the thing it named. A session landing residential with Monthly
   * open would otherwise take the bar away and leave that list rendering, with
   * no way back to the ledger short of a reload.
   */
  const activeTab = tabs.includes(tab) ? tab : 'visits';
  const listWindow = activeTab === 'upcoming' ? 'upcoming' : 'current';
  const sort = sortPick || (listWindow === 'upcoming' ? 'scheduled_asc' : 'scheduled_desc');
  const [notice, setNotice] = useState<string | null>(null);
  // One open at a time: the panel is a full-width row under the invoice it
  // belongs to, and several of them at once turn the table into a list of
  // paragraphs with no columns left to scan.
  const [expanded, setExpanded] = useState<string | null>(null);
  // Which rows the office has ticked for a bulk send, and what came back from
  // the last one. Both are scoped to the page on screen — see the reset below.
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [batch, setBatch] = useState<InvoiceBatchSendResult | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['invoices', { search, status, from, to, listWindow, sort, page }],
    queryFn: () =>
      invoiceApi.list({
        q: search || undefined,
        status: status || undefined,
        from: from || undefined,
        to: to || undefined,
        window: listWindow,
        sort,
        page,
        pageSize: 25,
      }),
    // Keeps the previous page on screen while the next one loads, so the
    // pager neither disappears nor forgets how many pages there are mid-click.
    // Never across tabs: a row from the other list, shown under this one's
    // name, could be opened, emailed or paid as if it belonged here.
    placeholderData: (previous, previousQuery) =>
      (previousQuery?.queryKey[1] as { listWindow?: string } | undefined)?.listWindow === listWindow
        ? previous
        : undefined,
  });

  const canWrite = can('payments:write');
  const canRefund = can('payments:refund');
  /** Whether the hand-written-invoice composer is open above the list. */
  const [composing, setComposing] = useState(false);
  // Both are needed only for the pay actions in the last column, so they stay
  // off the network for anyone without write access to invoices.
  const paymentsStatus = useQuery({
    queryKey: ['payments-status'],
    queryFn: () => paymentApi.status(),
    enabled: canWrite,
  });
  const methods = useQuery({
    queryKey: ['payment-methods', 'active'],
    queryFn: () => paymentApi.listMethods(),
    enabled: canWrite,
  });

  const currency = company?.currencyCode ?? DEFAULT_CURRENCY_CODE;
  // Each invoice reads its amount live off its own visit, in whatever currency
  // that visit was priced in — not necessarily the company's current one. A
  // manual invoice's figure is its own signed total: a credit memo shows
  // negative, which is exactly what it is.
  const invoiceMoney = (invoice: Invoice) => {
    const amount = invoice.kind === 'manual' ? invoice.totalCents : invoice.priceCents;
    return amount === null
      ? t('invoices.list.priceNotSet')
      : formatCents(amount, invoice.currencyCode ?? currency, getDateLocale());
  };

  function handlePaid(message: string) {
    setNotice(message);
    void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    // The open detail panel is the thing most likely to be *showing* the
    // payment that was just taken, so it has to be refetched too — its ledger
    // is a separate query from the list row's status badge. The credit form's
    // own ledger read goes with it, for the same reason.
    void queryClient.invalidateQueries({ queryKey: ['invoice'] });
    void queryClient.invalidateQueries({ queryKey: ['invoice-ledger'] });
  }

  const rows = query.data?.items ?? [];
  const sendable = rows.filter(canEmailInvoice);
  const chosen = sendable.filter((invoice) => selected.has(invoice.id));

  // A tick means "this row, the one I am looking at". Any change of filter,
  // sort or page replaces the rows underneath it, so the selection goes with
  // them rather than quietly carrying ids off a page nobody can see any more —
  // which is the difference between a bulk send you can check and one you
  // cannot. Cheap to redo: it is a click per row.
  useEffect(() => {
    setSelected(new Set());
    setBatch(null);
    setBulkError(null);
  }, [search, status, from, to, listWindow, sort, page]);

  const bulkSend = useMutation({
    mutationFn: () => invoiceApi.sendBatch(chosen.map((invoice) => invoice.id)),
    onSuccess: (result) => {
      setBatch(result);
      setNotice(null);
      setSelected(new Set());
      // Every send stamps `customer_notified_at` and may move nothing else, but
      // a settled or canceled invoice among them is exactly the sort of row
      // whose status the page is out of date about — which is why it was
      // skipped.
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['invoice'] });
    },
    onError: (caught: unknown) => {
      setBatch(null);
      setNotice(null);
      setBulkError(caught instanceof ApiRequestError ? caught.message : t('invoices.list.actionFailed'));
    },
  });

  const filtered = Boolean(search || status || from || to);
  const datesOrSortChanged = Boolean(from || to || sortPick);

  function clearFilters() {
    setSearch('');
    setStatus('');
    setFrom('');
    setTo('');
    setSortPick('');
    setPage(1);
  }

  return (
    <WorkPage
      title={t('invoices.list.title')}
      description={t('invoices.list.description')}
      actions={
        canWrite && activeTab === 'visits' && !composing ? (
          <Button variant="primary" onClick={() => setComposing(true)}>
            {t('invoices.new.open')}
          </Button>
        ) : null
      }
    >
      {/* Same tab pattern the reports page uses. Monthly invoices are their
          own list — see MonthlyInvoicesSection — so the filters, selection
          and pager below belong to the visit tab alone. A single tab is not a
          choice, so a residential company gets the ledger with no bar above it
          rather than one tab that cannot be switched away from. */}
      {tabs.length > 1 ? (
        <div role="tablist" aria-label={t('invoices.list.tabsAriaLabel')} className="mb-4 flex flex-wrap gap-1">
          {tabs.map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              id={`invoices-tab-${value}`}
              aria-selected={activeTab === value}
              onClick={() => {
                setTab(value);
                // A page number or a sort picked on one list means nothing on the other.
                setPage(1);
                setSortPick('');
              }}
              className={classes(
                'rounded-md px-3 py-2 font-medium',
                activeTab === value
                  ? 'bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
                  : 'text-[color:var(--color-muted)] hover:bg-[color:var(--color-surface)]',
              )}
            >
              {t(`invoices.list.tab.${value}`)}
            </button>
          ))}
        </div>
      ) : null}

      {activeTab === 'monthly' ? (
        <WorkSection title={t('invoices.list.tab.monthly')}>
          <MonthlyInvoicesSection />
        </WorkSection>
      ) : null}

      {notice && activeTab !== 'monthly' ? (
        <div className="mb-4">
          <Alert tone="success">{notice}</Alert>
        </div>
      ) : null}

      {activeTab === 'monthly' ? null : (
        <>
          {/* Visit invoices only: a hand-written invoice is dated today, so on
              Upcoming it would vanish from the list the moment it was saved. */}
          {composing && activeTab === 'visits' ? (
            <NewInvoiceComposer
              // Written in this location's currency, as the Worker stamps it.
              companyCurrency={locationCurrency}
              onCreated={(message) => {
                setComposing(false);
                handlePaid(message);
              }}
              onCancel={() => setComposing(false)}
            />
          ) : null}
          <form
            className="card mb-6 grid gap-x-4 gap-y-3 sm:grid-cols-3"
            onSubmit={(event) => {
              event.preventDefault();
              setPage(1);
            }}
            role="search"
          >
            <TextInput
              label={t('invoices.list.searchLabel')}
              type="search"
              placeholder={t('invoices.list.searchPlaceholder')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
            <Select
              label={t('invoices.list.statusLabel')}
              options={STATUS_OPTIONS}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            />
            <details className="self-start">
              <summary className="cursor-pointer py-2 font-semibold">
                {t(datesOrSortChanged ? 'invoices.list.datesAndSortApplied' : 'invoices.list.datesAndSort')}
              </summary>
              <TextInput
                label={t('invoices.list.fromLabel')}
                type="date"
                value={from}
                onChange={(event) => {
                  setFrom(event.target.value);
                  setPage(1);
                }}
              />
              <TextInput
                label={t('invoices.list.toLabel')}
                type="date"
                value={to}
                onChange={(event) => {
                  setTo(event.target.value);
                  setPage(1);
                }}
              />
              <Select
                label={t('invoices.list.sortLabel')}
                options={SORT_OPTIONS}
                value={sort}
                onChange={(event) => {
                  setSortPick(event.target.value);
                  setPage(1);
                }}
              />
            </details>
            {filtered || datesOrSortChanged ? (
              <div className="sm:col-span-3">
                <Button variant="ghost" onClick={clearFilters}>
                  {t('invoices.list.clearFilters')}
                </Button>
              </div>
            ) : null}
          </form>

          <WorkSection title={t(`invoices.list.tab.${activeTab}`)}>
            {batch ? (
              <div className="mb-4">
                <BulkEmailResult result={batch} />
              </div>
            ) : null}

            {bulkError ? (
              <div className="mb-4">
                <Alert tone="error">{bulkError}</Alert>
              </div>
            ) : null}

            {/* Only once something is ticked: an empty bar over every list would be a
          permanent invitation to a bulk send nobody came here for. */}
            {canWrite && chosen.length > 0 ? (
              <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-4 py-3">
                <span className="text-sm font-medium">{t('invoices.list.bulkSelected', { count: chosen.length })}</span>
                <Button
                  variant="primary"
                  busy={bulkSend.isPending}
                  onClick={() => {
                    setBatch(null);
                    setBulkError(null);
                    // Outward-facing and not undoable — twelve customers hear about
                    // it — so it asks once, with the number it is about to send.
                    if (window.confirm(t('invoices.list.bulkConfirm', { count: chosen.length }))) bulkSend.mutate();
                  }}
                >
                  {t('invoices.list.bulkSend', { count: chosen.length })}
                </Button>
                <Button variant="ghost" onClick={() => setSelected(new Set())}>
                  {t('invoices.list.bulkClear')}
                </Button>
              </div>
            ) : null}

            {query.isLoading ? <LoadingBlock label={t('invoices.list.loading')} /> : null}

            {query.isError ? (
              <Alert tone="error" title={t('invoices.list.loadErrorTitle')}>
                {(query.error as Error).message}
              </Alert>
            ) : null}

            {query.data && !query.isError && query.data.items.length === 0 ? (
              <WorkEmpty
                title={t(
                  filtered
                    ? 'invoices.list.emptyFilteredTitle'
                    : activeTab === 'upcoming'
                      ? 'invoices.list.emptyUpcomingTitle'
                      : 'invoices.list.emptyTitle',
                )}
              >
                <p>
                  {filtered
                    ? t('invoices.list.emptyFiltered')
                    : activeTab === 'upcoming'
                      ? t('invoices.list.emptyUpcoming')
                      : t('invoices.list.emptyDefault')}
                </p>
              </WorkEmpty>
            ) : null}

            {query.data && query.data.items.length > 0 ? (
              <TableScroll
                aria-label={t('invoices.list.caption', { count: query.data.total })}
                className="overflow-x-auto rounded-lg border border-[color:var(--color-line)] bg-white"
              >
                <table className="data-table w-full min-w-max border-collapse text-left">
                  <caption className="sr-only">{t('invoices.list.caption', { count: query.data.total })}</caption>
                  <thead>
                    <tr className="border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)]">
                      {canWrite ? (
                        <th scope="col" className="px-4 py-3">
                          {/* Ticks every row on this page that could be sent by hand,
                        which is never more than a page — and a page is the cap
                        the endpoint takes. */}
                          <input
                            type="checkbox"
                            className="h-5 w-5 rounded border-[color:var(--color-line)]"
                            aria-label={t('invoices.list.bulkSelectAll')}
                            disabled={sendable.length === 0}
                            checked={sendable.length > 0 && chosen.length === sendable.length}
                            onChange={(event) =>
                              setSelected(
                                event.target.checked ? new Set(sendable.map((invoice) => invoice.id)) : new Set(),
                              )
                            }
                          />
                        </th>
                      ) : null}
                      <th scope="col" className="px-4 py-3 text-sm font-semibold">
                        {t('invoices.list.columnInvoice')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-sm font-semibold">
                        {t('invoices.list.columnCustomer')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-sm font-semibold">
                        {t('invoices.list.columnStatus')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-sm font-semibold">
                        {t('invoices.list.columnDue')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-sm font-semibold">
                        {t('invoices.list.columnAmount')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-sm font-semibold">
                        {t('invoices.list.columnDocument')}
                      </th>
                      {canWrite ? (
                        <th scope="col" className="px-4 py-3 text-sm font-semibold">
                          {t('invoices.list.columnActions')}
                        </th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {query.data.items.map((invoice) => (
                      <Fragment key={invoice.id}>
                        <tr
                          data-selected={selected.has(invoice.id)}
                          className="border-b border-[color:var(--color-line)] align-top last:border-0"
                        >
                          {canWrite ? (
                            <td className="px-4 py-3">
                              {/* No box at all on a row that could not be sent by
                            hand either — a disabled one invites a click that
                            explains nothing. */}
                              {canEmailInvoice(invoice) ? (
                                <input
                                  type="checkbox"
                                  className="h-5 w-5 rounded border-[color:var(--color-line)]"
                                  aria-label={t('invoices.list.bulkSelectOne', { number: invoice.invoiceNumber })}
                                  checked={selected.has(invoice.id)}
                                  onChange={(event) =>
                                    setSelected((current) => {
                                      const next = new Set(current);
                                      if (event.target.checked) next.add(invoice.id);
                                      else next.delete(invoice.id);
                                      return next;
                                    })
                                  }
                                />
                              ) : null}
                            </td>
                          ) : null}
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold">{invoice.invoiceNumber}</span>
                              <InvoiceKindTag invoice={invoice} />
                            </div>
                            {/* The visit's own date — or, for a hand-written
                              invoice, the day it was issued: the date the
                              office knows it by. */}
                            <div className="text-sm text-[color:var(--color-muted)]">
                              {invoice.scheduledDate
                                ? formatNumericDate(invoice.scheduledDate)
                                : formatNumericInstant(invoice.issuedAt)}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="max-w-[18rem] table-text">{invoice.customerName}</div>
                            {invoice.propertyAddress ? (
                              <div className="max-w-[18rem] table-text text-sm text-[color:var(--color-muted)]">
                                <AddressLink address={invoice.propertyAddress}>
                                  {invoice.propertyAddress.split('\n')[0]}
                                </AddressLink>
                              </div>
                            ) : null}
                          </td>
                          <td className="px-4 py-3">
                            <InvoiceStatusBadge status={invoice.status} />
                          </td>
                          {/* When the money is due — issue date plus the customer's
                        net terms. A dash is the residential answer: due on
                        receipt, nothing to age against. */}
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {invoice.dueOn ? formatShortDate(invoice.dueOn) : '—'}
                          </td>
                          <td className="tabular px-4 py-3 text-right font-semibold">{invoiceMoney(invoice)}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap items-center gap-3">
                              {/* Fetched rather than navigated to, the same way the
                            payment receipt is: the invoice PDF is rendered on
                            demand, and the wait needs something on screen. */}
                              <PdfDownloadButton url={invoiceApi.pdfUrl(invoice.id)} variant="link">
                                {t('invoices.list.download')}
                              </PdfDownloadButton>
                              <Button
                                variant="ghost"
                                aria-expanded={expanded === invoice.id}
                                onClick={() => setExpanded((current) => (current === invoice.id ? null : invoice.id))}
                              >
                                {expanded === invoice.id ? t('invoices.list.hideDetails') : t('invoices.list.details')}
                              </Button>
                            </div>
                            {/* Beside the document rather than in the pay column: this
                          sends the invoice, it does not take money for it. */}
                            {canWrite ? <InvoiceEmailAction invoice={invoice} onSent={setNotice} /> : null}
                          </td>
                          {canWrite ? (
                            <td className="px-4 py-3">
                              {isCreditMemo(invoice) ? (
                                canRefund ? (
                                  <CreditRefundActions
                                    invoice={invoice}
                                    methods={methods.data?.items ?? []}
                                    companyCurrency={currency}
                                    onDone={handlePaid}
                                  />
                                ) : null
                              ) : (
                                <InvoicePayActions
                                  invoice={invoice}
                                  configured={paymentsStatus.data?.configured === true}
                                  tipsEnabled={paymentsStatus.data?.tips.enabled === true}
                                  methods={methods.data?.items ?? []}
                                  companyCurrency={currency}
                                  onDone={handlePaid}
                                />
                              )}
                            </td>
                          ) : null}
                        </tr>
                        {expanded === invoice.id ? (
                          <InvoiceDetailPanel
                            invoiceId={invoice.id}
                            columns={canWrite ? 8 : 6}
                            canWrite={canWrite}
                            canRefund={canRefund}
                            configured={paymentsStatus.data?.configured === true}
                            tipsEnabled={paymentsStatus.data?.tips.enabled === true}
                            methods={methods.data?.items ?? []}
                            companyCurrency={currency}
                            onChanged={handlePaid}
                          />
                        ) : null}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            ) : null}

            <Pagination page={page} totalPages={query.data?.totalPages} onPageChange={setPage} />
          </WorkSection>
        </>
      )}
    </WorkPage>
  );
}
